import React from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutManagerProps } from '../types';
import { useUserInteraction } from '../context/UserInteractionContext';
import { useChatContext } from '../context/ChatContext';
import ChatHead from './ChatHead';
import Avatar from './Avatar';
import './LayoutManager.css';

const LayoutManager: React.FC<LayoutManagerProps> = ({
  mode,
  children,
  onModeChange,
}) => {
  const { t } = useTranslation();
  const { markFirstChatOpen } = useUserInteraction();
  const { dispatch } = useChatContext();

  // Handle chat head click - opens to popup mode
  const handleChatHeadClick = () => {
    markFirstChatOpen(); // Mark user interaction for autoplay
    onModeChange('popup');
  };

  // Handle expand to fullscreen
  const handleExpandClick = () => {
    onModeChange('fullscreen');
  };

  // Handle minimize from fullscreen to popup
  const handleMinimizeClick = () => {
    onModeChange('popup');
  };

  // Handle close from popup to head
  const handleCloseClick = () => {
    onModeChange('head');
  };

  // Handle new chat session
  const handleNewChatClick = () => {
    // Generate new session ID (you can make this more sophisticated)
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Dispatch new chat session action (this clears messages and shows intro)
    dispatch({ type: 'NEW_CHAT_SESSION' });
    
    // Log for debugging (remove in production)
    console.log('🆕 New chat session started:', sessionId);
    
    // The initial message will be automatically loaded by the ChatApp component
    // when it detects that messages.length === 0
  };

  // Render only chat head when in head mode
  if (mode === 'head') {
    return (
      <ChatHead
        onClick={handleChatHeadClick}
        hasUnreadMessages={false} // You can make this dynamic based on state
        isOnline={true}
      />
    );
  }

  return (
    <div className={`layout-manager layout-manager--${mode}`}>
      {/* Header with controls */}
      <div className="layout-manager__header">
        <div className="layout-manager__title">
          <Avatar
            alt="AI Assistant"
            fallbackText="AI"
            size="small"
            className="avatar--assistant"
            isOnline={true}
          />
          AI Assistant
        </div>
        <div className="layout-manager__controls">
          {/* New Chat Button - always visible */}
          <button
            className="layout-manager__control-btn layout-manager__new-chat-btn"
            onClick={handleNewChatClick}
            aria-label={t('common.startNewChat')}
            title={t('common.startNewChat')}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
            </svg>
          </button>
          
          {mode === 'popup' && (
            <>
              <button
                className="layout-manager__control-btn layout-manager__expand-btn"
                onClick={handleExpandClick}
                aria-label="Expand to fullscreen"
                title="Expand to fullscreen"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z"/>
                </svg>
              </button>
              <button
                className="layout-manager__control-btn layout-manager__close-btn"
                onClick={handleCloseClick}
                aria-label="Minimize to chat head"
                title="Minimize to chat head"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                </svg>
              </button>
            </>
          )}
          {mode === 'fullscreen' && (
            <button
              className="layout-manager__control-btn layout-manager__minimize-btn"
              onClick={handleMinimizeClick}
              aria-label="Minimize to popup"
              title="Minimize to popup"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M6 10v4H2v-4h4zm8 0v4h-4v-4h4zM6 2v4H2V2h4zm8 0v4h-4V2h4z"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Main content area */}
      <div className="layout-manager__content">
        {children}
      </div>

      {/* Resize handle for popup mode */}
      {mode === 'popup' && (
        <div className="layout-manager__resize-handle" />
      )}
    </div>
  );
};

export default LayoutManager;